import Ext from "Ext";
import $ from "jquery";
import _ from "underscore";
import HierarchicalSelectorPanel from "../controls/selectorControls/at_hierarchical_selector_panel";
import TreeNode from "../hierarchySelector/tree/TreeNode";
import TreeRootNode from "../hierarchySelector/tree/TreeRootNode";
import GroupType from "../enums/GroupType";
import GroupSelectorSwitcher from "../controls/selectorControls/at_group_selector_switcher";
import StringIndex from "../util/StringIndex";
import TreeNodeSet from "../hierarchySelector/tree/TreeNodeSet";
import TwoLevelSelection from "../controls/selectorControls/two_level_selection";
import ShowUserGroupsStrategy from "./userSelectorGroupsStrategies/ShowUserGroupsStrategy";
import HideUserGroupsStrategy from "./userSelectorGroupsStrategies/HideUserGroupsStrategy";
import {makeMapAndOrderForEntitiesArray} from "../atUtils";
import {getArrayFromCookies} from "../atBrowserUtils";
import {buildUserSelectorTreeWithTimeZoneGroupsData, buildUserSelectorTreeWithUserGroupsData} from "./user_group_selector_util";
import UserSelectorTreeBuilderObserver from "./builder/UserSelectorTreeBuilderObserver";
import SimpleUsersAndGroupsSelectionHelper from "../controls/selectorControls/selectionHelpers/simple_users_and_groups_selection_helper";
import UserGroupNodeRenderer from "../hierarchySelector/userSelector/UserGroupNodeRenderer";
import TimeZoneGroupNodeRenderer from "../hierarchySelector/userSelector/TimeZoneGroupNodeRenderer";
import UserSelectorEmptyMessageBuilder from "../hierarchySelector/userSelector/emptyMessagesBuilders/UserSelectorEmptyMessageBuilder";
import UserNodeRenderer from "../hierarchySelector/userSelector/UserNodeRenderer";
import DisabledUsersParentNodeRenderer from "../hierarchySelector/userSelector/DisabledUsersParentNodeRenderer";
import messageResource from '../messageResource'
import Switcher from '../components/Switcher'

/**
 * Selector panel for the users list.
 *
 * Depends on:
 * <code>/js/selectorControls/at_generic_selector_panel.js</code>
 * <code>/js/controls/selectorControls/at_tree_node_helper.js</code>
 * <code>/js/controls/selectorControls/at_user_tree_node_helper.js</code>
 * <code>/js/message_resources.js</code> with prefixes "users_selector", "usersselector"
 *
 * @constructor
 */
const UsersSelectorPanel = Ext.extend(HierarchicalSelectorPanel, {
    /**
     * @cfg {Boolean} Specifies whether item's email should be checked when item is displayed in the list
     */
    checkEmail: false,

    optionValue: true,

    allItemsOptionValue: true,
    customOptionValue: false,
    /**
     * @cfg {int} maximum length of each item label, the rest of symbols will be truncated. If equal null, the whole
     * name will be shown
     */
    itemLabelMaxLength: null,

    allItemsOptionLabelKey: "users_selector.all_users",
    customOptionLabelKey: "users_selector.selected_users",
    showInactiveLabelKey: "users_selector.show_disabled",

    labelItemNounSingularKey: "users_selector.label.singular",
    labelItemNounPluralKey: "users_selector.label.plural",



    userSelectorGroupsStrategy: null,

    collapsedCookiesKey: "",

    groupBySettingCookieName: "userSelectorGroupBySetting",

    createComponents: function(){

        this.collapsedUserGroupsCookiesKey = this.collapsedCookiesKey + "CollapsedUserGroups";
        this.collapsedTimeZoneGroupsCookiesKey = this.collapsedCookiesKey + "CollapsedTimeZoneGroups";
        UsersSelectorPanel.superclass.createComponents.call(this);
        this.viewByPanel = new GroupSelectorSwitcher();

        var self = this;
        this.selectAllButton.on("click", function() {
            var selectionToProcess = self.groupType == GroupType.USER_GROUP ? self.timeZoneSelection : self.userGroupSelection;
            if (!selectionToProcess)
                return;

            for (var i = 0; i < self.users.length; i++)
            {
                var userId = self.users[i].id;
                if (self.groupType == GroupType.USER_GROUP)
                    self.addUserIdToSelection(userId, selectionToProcess, GroupType.TIME_ZONE_GROUP);
                else
                    self.addUserIdToSelection(userId, selectionToProcess, GroupType.USER_GROUP);
            }
        });

        this.clearButton.on("click", function() {
            self.clearPreviousSelection();
        });
        this.addClass("user-selector-panel");
    },

    makeSelectionTree: function() {
        //do nothing
    },

    getFilterWordsPopupCoordinates : function ()
    {
        return {
            x : 2,
            y : -4
        }
    },

    saveCollapsedNodesIntoCookies: function() {
        var cookiesKey = this.groupType == GroupType.USER_GROUP ? this.collapsedUserGroupsCookiesKey : this.collapsedTimeZoneGroupsCookiesKey;
        this.userSelectorGroupsStrategy.saveCollapsedNodesIntoCookies(this.selectorTree, cookiesKey);
    },

    canExpandCollapseNodes: function() {
        return this.userSelectorGroupsStrategy.canExpandCollapseNodes();
    },

    getSelection: function(isFullFormed) {
        return this.userSelectorGroupsStrategy.makeSelectionFromNodeSet(this.getSelectedNodeSet(), isFullFormed, this.mapSelectionGroupsToUsers(), this.getGroupType());
    },

    mapSelectionGroupsToUsers: function() {
        return false
    },

    getGroupType: function() {
        return this.isCustomOptionSelected() && this.groupType ? this.groupType : null;
    },

    getTreePanelClasses: function()
    {
        var classes = UsersSelectorPanel.superclass.getTreePanelClasses.call( this );
        return classes.concat( this.userSelectorGroupsStrategy.getAdditionalTreePanelClasses() );
    },

    getOnlyUsersSelection: function() {
        return this.userSelectorGroupsStrategy.makeOnlyUsersSelection(this.getSelectedNodeSet());
    },

    getAllSelectedUsers: function() {
        return this.userSelectorGroupsStrategy.makeAllSelectedUsersSelection(this.getSelectedNodeSet());
    },

    makeNodeSet: function(selection) {
        return this.userSelectorGroupsStrategy.makeNodeSet(selection, this.selectorTree.getRootNode());
    },

    makeSelectionHelper: function() {
        this.selectionHelper = new SimpleUsersAndGroupsSelectionHelper({
            itemNounSingularKey: this.labelItemNounSingularKey,
            itemNounPluralKey: this.labelItemNounPluralKey,
            showInactiveCount: true,
            showSingleItemInactiveText: false,
            userSelectorGroupsStrategy: this.userSelectorGroupsStrategy,
            maxCharacters: this.labelMaxChars
        });
    },

    createEmptyMessageBuilder: function() {
        return new UserSelectorEmptyMessageBuilder(this.userSelectorGroupsStrategy);
    },

    getNodeRenderers: function()
    {
        var result = {};
        this.userNodeRenderer = this.getUserNodeRenderer();
        result[TreeNode.TYPE_USER] = this.userNodeRenderer;
        result[TreeNode.TYPE_GROUP] = this.groupType == GroupType.USER_GROUP ?
                                      this.getUserGroupNodeRenderer() : this.getTimeZoneGroupNodeRenderer();
        result[TreeNode.TYPE_DISABLED_USERS_PARENT] = this.getDisabledUsersParentNodeRenderer();
        return result;
    },

    createTree: function() {
        return this.userSelectorGroupsStrategy.createTree(this.rootNode, this.createEmptyMessageBuilder(), this.getCollapsedNodeIdsFromCookies(this.groupType));
    },

    getCollapsedNodeIdsFromCookies: function(groupType)
    {
        var cookiesKey = groupType == GroupType.USER_GROUP ? this.collapsedUserGroupsCookiesKey : this.collapsedTimeZoneGroupsCookiesKey;
        return this.canExpandCollapseNodes() ? getArrayFromCookies(cookiesKey) : [];
    },

    processFirstPanelOpen: function() {
        UsersSelectorPanel.superclass.processFirstPanelOpen.call(this);
        this.buildSelectorTree();
        this.initialTreeSet =  this.selectorTree.getSelectionController().getInitiallySelectedNodeSet();
        if (this.viewByPanel)
            this.viewByPanel.updateUI(this.groupType);
    },

    isTopNodeLevel: function(treeNode) {
        return this.userSelectorGroupsStrategy.isUserGroupNodeLevel(treeNode);
    },

    isLowNodeLevel: function(treeNode) {
        return this.userSelectorGroupsStrategy.isUserNodeLevel(treeNode);
    },

    isHiddenNodeSelected: function(treeNode) {
        return this.checkHiddenNodeSelectionTemplate(treeNode, function()
        {
            return treeNode.getType() == TreeNode.TYPE_DISABLED_USERS_PARENT || !treeNode.getValue().isActive()
        });
    },

    getUserGroupNodeRenderer: function() {
        return new UserGroupNodeRenderer(this.selectorTree, this.selectorFilter, this.getExpandCollapseCallback());
    },

    getTimeZoneGroupNodeRenderer: function() {
        return new TimeZoneGroupNodeRenderer(this.selectorTree, this.selectorFilter, this.getExpandCollapseCallback());
    },

    getUserNodeRenderer: function() {
        return new UserNodeRenderer( this.selectorTree, this.selectorFilter, this.getSelectionHelper() );
    },

    getDisabledUsersParentNodeRenderer: function() {
        return new DisabledUsersParentNodeRenderer( this.selectorTree, this.selectorFilter, this.getExpandCollapseCallback() );
    },

    getState: function()
    {
        return Ext.applyIf ({  labelMaxChars: this.labelMaxChars,
                                treeItemTooltipMessageParams: this.treeItemTooltipMessageParams,
                                checkEmail: this.checkEmail,
                                itemLabelMaxLength: this.itemLabelMaxLength,
                                groupType: this.groupType },
                            UsersSelectorPanel.superclass.getState.call(this));

    },

    loadState: function(state, doNotUpdateGroupType)
    {
        if (!doNotUpdateGroupType)
            this.setGroupType(state.groupType);
        UsersSelectorPanel.superclass.loadState.call(this, state);
    },

    loadPermanentState: function(state) {
        UsersSelectorPanel.superclass.loadPermanentState.call(this, state);
        this.labelMaxChars = state.labelMaxChars;
        this.treeItemTooltipMessageParams = state.treeItemTooltipMessageParams;
        this.checkEmail = state.checkEmail;
        this.itemLabelMaxLength = state.itemLabelMaxLength;
    },

    isStateChanged: function() {
        return UsersSelectorPanel.superclass.isStateChanged.call(this) || this.groupTypeInit != this.groupType;
    },

    setPredefinedParameters: function(state) {
        this.showInactive = state.showInactive;
        this.setInitialOptionValue(state.optionValue);
        if (this.isAlreadyShown())
        {
            this.markPredefinedNodesAsSelected(state.selection);
            this.updateInitialTreeSet();
        }
    },

    updateInitialTreeSet : function(){
        this.initialTreeSet =  this.selectorTree.getSelectionController().getInitiallySelectedNodeSet();
    },

    updateInitialGroupType : function() 
    {
        this.groupTypeInit = this.groupType;
    },
    
    groupByUserGroup: function ()
    {
        var settings = this.collectCurrentSettings();

        this.groupType = GroupType.USER_GROUP;
        this.prepareForGroupByChange();

        this.changeUserSelectorGroupsStrategy( this.initShowGroupsStrategy() );

        this.buildGroupByUserGroupsTreeData();

        this.setTreeData(this.groupByUserGroupsTreeData);

        this.recreateTreeSetSelectorSettingsAndBuildTree(settings);

        this.viewByPanel.updateUI(this.groupType);
        this.saveGroupBySettingsToCookie("groupByUserGroups");
    },

    groupByTimeZoneGroup: function ()
    {
        var settings = this.collectCurrentSettings();

        this.groupType = GroupType.TIME_ZONE_GROUP;
        this.prepareForGroupByChange();

        this.changeUserSelectorGroupsStrategy( this.initShowGroupsStrategy() );

        this.buildGroupByTimeZoneGroupsTreeData();

        this.setTreeData(this.groupByTimeZoneGroupsTreeData);

        this.recreateTreeSetSelectorSettingsAndBuildTree(settings);

        this.viewByPanel.updateUI(this.groupType);
        this.saveGroupBySettingsToCookie("groupByTimeZoneGroups");
    },

    getUsersToShow: function() {
        return this.users
    },

    buildGroupByUserGroupsTreeData: function () {
        var rootNode = new TreeRootNode();
        var searchIndex = new StringIndex();
        var allActiveSet = new TreeNodeSet();

        var groupsData = this.userGroupsEnabled ? makeMapAndOrderForEntitiesArray( this.userGroups ) : null;
        buildUserSelectorTreeWithUserGroupsData( this.getUsersToShow(), groupsData, rootNode,
                                             this.createUserSelectorTreeBuilderObserver( searchIndex, allActiveSet ),
                                             this.getUsersFilter() );

        this.groupByUserGroupsTreeData = {
            rootNode: rootNode,
            searchIndex: searchIndex,
            allActiveSet: allActiveSet
        };
    },

    buildGroupByTimeZoneGroupsTreeData: function () {
        var rootNode = new TreeRootNode();
        var searchIndex = new StringIndex();
        var allActiveSet = new TreeNodeSet();

        var groupsData = this.timeZoneGroupsEnabled ? makeMapAndOrderForEntitiesArray( this.timeZoneGroups ) : null;
        buildUserSelectorTreeWithTimeZoneGroupsData( this.getUsersToShow(), groupsData, rootNode,
                                                     this.createUserSelectorTreeBuilderObserver( searchIndex, allActiveSet ),
                                                     this.getUsersFilter());

        this.groupByTimeZoneGroupsTreeData = {
            rootNode: rootNode,
            searchIndex: searchIndex,
            allActiveSet: allActiveSet
        };
    },

    createUserSelectorTreeBuilderObserver: function(searchIndex, allActiveSet) {
        return new UserSelectorTreeBuilderObserver( searchIndex, allActiveSet )
    },

    getUsersFilter: function()
    {
        return null;
    },

    setGroupByUserGroupsTreeData: function(groupByUserGroupsTreeData)
    {
        this.groupByUserGroupsTreeData = groupByUserGroupsTreeData;
    },

    setGroupByTimeZoneGroupsTreeData: function(groupByTimeZoneGroupsTreeData)
    {
        this.groupByTimeZoneGroupsTreeData = groupByTimeZoneGroupsTreeData;
    },

    setTreeData: function ( treeData )
    {
        this.rootNode = treeData.rootNode;
        this.searchIndex = treeData.searchIndex;
        this.allActiveSet = treeData.allActiveSet;
    },

    setGroupType: function(newGroupType)
    {
        if (!newGroupType)
        {
            var groupBySettingFromCookies = $.cookie(this.groupBySettingCookieName);
            newGroupType = this.getGroupTypeFromString(groupBySettingFromCookies);
        }

        if (newGroupType == GroupType.USER_GROUP && !this.userGroupsEnabled)
            newGroupType = this.timeZoneGroupsEnabled ? GroupType.TIME_ZONE_GROUP : null;

        if (newGroupType == GroupType.TIME_ZONE_GROUP && !this.timeZoneGroupsEnabled)
            newGroupType = this.userGroupsEnabled ? GroupType.USER_GROUP : null;

        if (this.isAlreadyShown())
        {
            this.regroupUsers(newGroupType);
        }
        else
        {
            this.groupType = newGroupType;
            this.initTreeData();
            this.createSelectorTree();
            this.viewByPanel.updateUI(this.groupType);
            this.groupTypeInit = this.groupType;
        }
    },

    regroupUsers: function(newGroupType)
    {
        if (newGroupType == GroupType.TIME_ZONE_GROUP)
            this.groupByTimeZoneGroup();
        if (newGroupType == GroupType.USER_GROUP)
            this.groupByUserGroup();
    },

    clearPreviousSelection: function()
    {
        this.userGroupSelection = null;
        this.timeZoneSelection = null;
    },

    initTreeData: function()
    {
        if (this.groupType == GroupType.USER_GROUP)
        {
            this.userSelectorGroupsStrategy = this.userGroupsEnabled ? this.initShowGroupsStrategy() : this.initHideGroupsStrategy();
            if (!this.groupByUserGroupsTreeData)
                this.buildGroupByUserGroupsTreeData();
            this.setTreeData(this.groupByUserGroupsTreeData);
        }
        else
        {
            this.userSelectorGroupsStrategy = this.timeZoneGroupsEnabled ? this.initShowGroupsStrategy() : this.initHideGroupsStrategy();
            if (!this.groupByTimeZoneGroupsTreeData)
                this.buildGroupByTimeZoneGroupsTreeData();
            this.setTreeData(this.groupByTimeZoneGroupsTreeData);
        }
    },

    getGroupTypeFromString: function(groupBySettingsString)
    {
        if (groupBySettingsString == "groupByTimeZoneGroups")
            return GroupType.TIME_ZONE_GROUP;
        else
            return GroupType.USER_GROUP;
    },

    getCustomOptionSection: function()
    {
        var customOptionControls = [];
        // from left to right
        customOptionControls.push(this.viewByPanel);
        customOptionControls.push(this.getCustomOptionButtonsSection());

        this.customControlsContainer = new Ext.Container({
            cls: "userSelectorCustomControlsContainer",
            autoEl: "div",
            items: customOptionControls
        });
        return [this.customOption, this.customControlsContainer];
    },

    getCustomOptionButtonsSection: function()
    {
        var additionalClass = "";
        if (!this.userGroupsEnabled && !this.timeZoneGroupsEnabled)
        {
            additionalClass = "noGroups";
        }
        return new Ext.Panel({
            cls: "buttonsPanel " + additionalClass,
            autEl: "div",
            items: this.getCustomOptionButtons()
        });
    },

    getCustomOptionButtons: function()
    {
        var buttons = [];
        buttons.push(this.filterButton, this.makeVerticalSeparator());
        buttons.push(this.hideNonSelectedButton, this.makeVerticalSeparator());
        buttons.push(this.selectAllButton, this.makeVerticalSeparator());
        buttons.push(this.clearButton);
        if (this.canExpandCollapseNodes())
        {
            buttons.push(this.makeVerticalSeparator(), this.collapseAllButton, this.expandAllButton);
        }

        return buttons;
    },

    initializeViewByPanel: function()
    {
        var self = this;
        this.viewByPanel.init(this.userGroupsEnabled,
                              this.timeZoneGroupsEnabled,
                              function() { self.treeActionHandler($.proxy(self.groupByUserGroup, self), true, false) },
                              function() { self.treeActionHandler($.proxy(self.groupByTimeZoneGroup, self), true, false) }
        );
    },

    initialize: function (userGroupsEnabled, timeZoneGroupsEnabled, users, userGroups, timeZoneGroups)
    {
        this.userGroupsEnabled = userGroupsEnabled;
        this.timeZoneGroupsEnabled = timeZoneGroupsEnabled;
        this.users = users;
        this.userGroups = userGroups;
        this.timeZoneGroups = timeZoneGroups;
        this.groupByUserGroupsTreeData = null;

        this.setGroupType(null);

        this.createGroupsUsersMaps();
        this.initializeViewByPanel();

        if (this.showShowInactiveCheckbox)
        {
            var anyDisabled = _.any(users, function(u) { return !u.isActive() });
            if (!anyDisabled)
                this.showInactiveCheckbox.hide();
        }
    },

    createGroupsUsersMaps: function()
    {
        this.usersMap = {};
        this.userGroupsUsersMap = {};
        this.timeZoneGroupsUsersMap = {};
        this.userGroupsDisabledUsersMap = {};
        this.timeZoneGroupsDisabledUsersMap = {};

        for (var i = 0; i < this.users.length; i++)
        {
            var user = this.users[i];
            this.usersMap[user.id] = user;

            var userGroupId = user.userGroupId;
            if (!this.userGroupsUsersMap[userGroupId])
                this.userGroupsUsersMap[userGroupId] = [];
            this.userGroupsUsersMap[userGroupId].push(user.id);

            var timeZoneGroupId = user.timeZoneGroupId;
            if (!this.timeZoneGroupsUsersMap[timeZoneGroupId])
                this.timeZoneGroupsUsersMap[timeZoneGroupId] = [];
            this.timeZoneGroupsUsersMap[timeZoneGroupId].push(user.id);

            if (!user.isActive())
            {
                if (!this.userGroupsDisabledUsersMap[userGroupId])
                    this.userGroupsDisabledUsersMap[userGroupId] = [];
                this.userGroupsDisabledUsersMap[userGroupId].push(user.id);

                if (!this.timeZoneGroupsDisabledUsersMap[timeZoneGroupId])
                    this.timeZoneGroupsDisabledUsersMap[timeZoneGroupId] = [];
                this.timeZoneGroupsDisabledUsersMap[timeZoneGroupId].push(user.id);
            }
        }
    },

    initHideGroupsStrategy: function ()
    {
        return new HideUserGroupsStrategy();
    },

    initShowGroupsStrategy: function ()
    {
        var self = this;
        return new ShowUserGroupsStrategy({
                                              getGroupIdFromUserCallback: function(user) {return self.groupType == GroupType.USER_GROUP ? user.getUserGroupId() : user.getTimeZoneGroupId();},
                                              noSelectedNodesMessageKey: self.groupType == GroupType.USER_GROUP  ?
                                                                         "users_selector.hide_not_selected.no_users_or_user_groups_selected" :
                                                                         "users_selector.hide_not_selected.no_users_or_time_zone_groups_selected",
                                              noNodesMatchingSearchMessageKey: self.groupType == GroupType.USER_GROUP  ?
                                                                               "users_selector.users_or_user_groups_no_results_in_search" :
                                                                               "users_selector.users_or_time_zone_groups_no_results_in_search"
                                          });
    },

    collectCurrentSettings: function ()
    {
        var currentState = this.getState();
        var currentSelectionWithHiddenUsers = this.getSelection(false, true);

        var groupsUsersMap;
        var prevSelection;
        if (this.groupType == GroupType.USER_GROUP) {
            groupsUsersMap = this.userGroupsUsersMap;
            prevSelection = this.timeZoneSelection;
            this.userGroupSelection = new TwoLevelSelection(currentSelectionWithHiddenUsers.getFirstLevel(), currentSelectionWithHiddenUsers.getSecondLevel())
        } else {
            groupsUsersMap = this.timeZoneGroupsUsersMap;
            prevSelection = this.userGroupSelection;
            this.timeZoneSelection = new TwoLevelSelection(currentSelectionWithHiddenUsers.getFirstLevel(), currentSelectionWithHiddenUsers.getSecondLevel())
        }

        var selection;
        if (prevSelection) {
            selection = prevSelection;
        } else {
            var selectedUserIds = currentSelectionWithHiddenUsers.getSecondLevel();
            var selectedGroupIds = currentSelectionWithHiddenUsers.getFirstLevel();

            for (var i = 0; i < selectedGroupIds.length; i++)
                selectedUserIds = selectedUserIds.concat(groupsUsersMap[selectedGroupIds[i]]);

            selection = new TwoLevelSelection([], selectedUserIds);
        }

        return {
            showInactiveCheckboxValue: currentState.showInactive,
            allUsers: currentState.optionValue,
            selection: selection,
            searchString: this.filterWordsField ? this.filterWordsField.getSearchString() : null,
            hideNonSelectedButtonChecked: this.hideNonSelectedButtonChecked,
            optionValueInit: this.optionValueInit,
            initialSelection: this.userSelectorGroupsStrategy.makeSelectionFromNodeSet(
                    this.selectorTree.getSelectionController().getInitiallySelectedNodeSet(), false, true),
            labelMaxChars: currentState.labelMaxChars,
            treeItemTooltipMessageParams: currentState.treeItemTooltipMessageParams,
            checkEmail: currentState.checkEmail,
            itemLabelMaxLength: currentState.itemLabelMaxLength
        };
    },

    /* This method must be called after selection is got */
    prepareForGroupByChange: function ()
    {
        this.alreadyShown = false;
        this.fireSelectionChangeAllowed = false;
    },

    changeUserSelectorGroupsStrategy: function(newUserSelectorGroupsStrategy)
    {
        this.userSelectorGroupsStrategy = newUserSelectorGroupsStrategy;
        this.selectionHelper.setUserSelectorGroupsStrategy(newUserSelectorGroupsStrategy);
    },

    /*creates, sets given settings to selector panel and builds selector tree*/
    recreateTreeSetSelectorSettingsAndBuildTree: function(setting)
    {
        this.createSelectorTree();
        $(this.treePanel).empty();
        this.setSettingsBeforeTreeRebuilding(setting);
        this.buildSelectorTree();
        this.setSettingsAfterTreeRebuilding(setting);
    },

    /*
     1. Use this method to configure filtering, showInactive checkbox, etc. while regrouping.
     2. No refiltering is performed while configuring selector filter here, tree will be built considering filter settings
     3. Set manually the value of showInactiveCheckbox to selectorFilter, because this value doesn't change while regrouping and oncheck-event is not called
     */
    setSettingsBeforeTreeRebuilding: function (settings)
    {
        this.loadState(this.getStateFromSettings(settings), true);
        this.selectorFilter.setShowInactive(settings.showInactiveCheckboxValue);
        if (settings.searchString)
            this.selectorFilter.setSearchString(settings.searchString);
    },

    /**
     * We have settings, state and conversion between them.
     * So if you reimplement <b>collectSettings</b> probably you need to reimplement this method and <b>loadState</b>
     */
    getStateFromSettings: function (settings)
    {
        return {
            optionValue: settings.allUsers,
            showInactive: settings.showInactiveCheckboxValue,
            selection: settings.selection,
            groupType: this.groupType,
            labelMaxChars: settings.labelMaxChars,
            treeItemTooltipMessageParams: settings.treeItemTooltipMessageParams,
            checkEmail: settings.checkEmail,
            itemLabelMaxLength: settings.itemLabelMaxLength
        };
    },

    /*
     Use this method to configure initial state - state when selector was opened (for correct work of reset link).
     */
    setSettingsAfterTreeRebuilding: function (settings)
    {
        this.optionValueInit = settings.optionValueInit;
        this.markPredefinedNodesAsSelected(settings.initialSelection);
        if (this.filterWordsPopup && this.filterWordsPopup.isShown())
        {
            this.selectorTree.expandAll();
            this.saveCollapsedNodesIntoCookies();
        }
        this.setHideNonSelectedButtonState(settings.hideNonSelectedButtonChecked);
    },

    createSelectorTree: function()
    {
        UsersSelectorPanel.superclass.createSelectorTree.call(this);
        this.addSelectorTreeChangeListener();
    },

    beforeResetAction: function(){
        const self = this
        if (this.groupTypeInit && this.groupType !== GroupType.USER_GROUP && this.groupTypeInit === GroupType.USER_GROUP) {
            self.groupByUserGroup()
        }
        if (this.groupTypeInit && this.groupType !== GroupType.TIME_ZONE_GROUP && this.groupTypeInit === GroupType.TIME_ZONE_GROUP) {
            self.groupByTimeZoneGroup()
        }
    },

    afterResetAction: function()
    {
        UsersSelectorPanel.superclass.afterResetAction.call(this);
        this.clearPreviousSelection();
        this.viewByPanel.updateUI(this.groupTypeInit);
    },

    setInitialValuesFromCurrent: function()
    {
        UsersSelectorPanel.superclass.setInitialValuesFromCurrent.call(this);
        this.groupTypeInit = this.groupType;
    },

    saveGroupBySettingsToCookie: function (value)
    {
        $.cookie(this.groupBySettingCookieName, value, { expires: 365 });
    },

    addSelectorTreeChangeListener: function()
    {
        var self = this;
        this.selectorTree.getSelectionController().addListener( {
            nodeDeselected: function (treeNode) {
                var selectionToProcess = self.groupType == GroupType.USER_GROUP ? self.timeZoneSelection : self.userGroupSelection;
                if (!selectionToProcess)
                    return;

                var selectionGroupType = selectionToProcess.groupType;

                if (self.isLowNodeLevel(treeNode)) {
                    self.removeUserIdFromSelection(treeNode.value.id, selectionToProcess, selectionGroupType);
                } else {
                    var usersFromGroup = self.getUsersFromTreeNode(treeNode);
                    for (var i = 0; i < usersFromGroup.length; i++) {
                        self.removeUserIdFromSelection(usersFromGroup[i], selectionToProcess, selectionGroupType);
                    }
                }
            },
            nodeSelected: function (treeNode) {
                var selectionToProcess = self.groupType == GroupType.USER_GROUP ? self.timeZoneSelection : self.userGroupSelection;
                if (!selectionToProcess)
                    return;

                var selectionGroupType = selectionToProcess.groupType;

                if (self.isLowNodeLevel(treeNode)) {
                    self.addUserIdToSelection(treeNode.value.id, selectionToProcess, selectionGroupType);
                } else {
                    var usersFromGroup = self.getUsersFromTreeNode(treeNode);
                    for (var i = 0; i < usersFromGroup.length; i++) {
                        self.addUserIdToSelection(usersFromGroup[i], selectionToProcess, selectionGroupType);
                    }
                }
            }
        } );
    },

    getUsersFromTreeNode: function(treeNode)
    {
        var groupsUsersMap;
        var groupId;
        if (treeNode.getType() == TreeNode.TYPE_GROUP)
        {
            groupsUsersMap = this.groupType == GroupType.USER_GROUP ? this.userGroupsUsersMap : this.timeZoneGroupsUsersMap;
            groupId = treeNode.getValue().id;
        }
        else if (treeNode.getType() == TreeNode.TYPE_DISABLED_USERS_PARENT)
        {
            groupsUsersMap = this.groupType == GroupType.USER_GROUP ? this.userGroupsDisabledUsersMap : this.timeZoneGroupsDisabledUsersMap;
            groupId = treeNode.getParent().getValue().id;
        }

        return groupsUsersMap[groupId];
    },

    addUserIdToSelection: function(userId, selection, selectionGroupType)
    {
        var user = this.usersMap[userId];
        var groupId = selectionGroupType == GroupType.USER_GROUP ? user.userGroupId : user.timeZoneGroupId;

        // do not add userId if user is in already selected group
        if ($.inArray(groupId, selection.getFirstLevel()) != -1)
            return;

        if ($.inArray(userId, selection.getSecondLevel()) == -1)
            selection.getSecondLevel().push(userId);
    },

    removeUserIdFromSelection: function(userId, selection, selectionGroupType)
    {
        selection.secondLevel = $.grep(selection.secondLevel, function( id ) {
            return userId != id;
        });

        var user = this.usersMap[userId];
        var groupId = selectionGroupType == GroupType.USER_GROUP ? user.userGroupId : user.timeZoneGroupId;

        var needToDeleteGroupFromSelection = $.inArray(groupId, selection.getFirstLevel()) != -1;
        if (!needToDeleteGroupFromSelection)
            return;

        selection.firstLevel = $.grep(selection.firstLevel, function( id ) {
            return groupId != id;
        });

        var groupsUsersMap = selectionGroupType == GroupType.USER_GROUP ? this.userGroupsUsersMap : this.timeZoneGroupsUsersMap;
        var usersFromGroup = groupsUsersMap[groupId];
        for (var i = 0; i < usersFromGroup.length; i++)
        {
            var userFromGroupId = usersFromGroup[i];
            if (userFromGroupId != userId) {
                if ($.inArray(userFromGroupId, selection.getSecondLevel()) == -1)
                    selection.getSecondLevel().push(userFromGroupId);
            }
        }
    },

    resetNodes: function()
    {
        if (this.groupTypeInit == GroupType.USER_GROUP) 
            this.groupByUserGroup();
        else
        if (this.groupTypeInit == GroupType.TIME_ZONE_GROUP)
            this.groupByTimeZoneGroup();
        
        this.selectorTree.resetNodes( this.initialTreeSet );
    },

    onGenericOptionSelect: function(option, checked) {

        if(checked)
        {
            this.addClass("hideCustomOptionField");
        }
        UsersSelectorPanel.superclass.onGenericOptionSelect.call(this, option, checked);
    },

    onCustomOptionSelect: function(option, checked) {
        if(checked)
        {
            this.removeClass("hideCustomOptionField");
            if(!$.isEmptyObject(this.selectorTree))
            {
                this.selectorTree.refreshScroller();
            }
        }
        UsersSelectorPanel.superclass.onCustomOptionSelect.call(this, option, checked);
    },

    setAllUsersIfNoneSelected: function ()
    {
        if( this.getSelectedNodeSet().isEmpty() && this.isCustomOptionSelected() )
            this.selectOption(this.allItemsOptionValue);
    }
});

Ext.reg("at_users_selector_panel", UsersSelectorPanel);

export default UsersSelectorPanel;


// WEBPACK FOOTER //
// ./web/js/usersSelector/at_users_selector_panel.js